<template>
  <div class="grating-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$d sinθ = kλ$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>主极大位置 (k=1)</label>
            <span class="val">θ = {{ (Math.asin(Math.min(1, (1 * wavelength / 1000) * gratingDensity / 100)) * 180 / Math.PI).toFixed(1) }}°</span>
          </div>
          <div class="hud-item orange">
             <label>模式</label>
             <span class="val">{{ isWhiteLight ? '复色白光 (色散)' : '单色激光' }}</span>
          </div>
        </div>

        <svg width="100%" height="260" viewBox="0 0 400 260" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-light"><feGaussianBlur stdDeviation="2" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <!-- 这里的渐变模拟一级光谱色散 -->
             <linearGradient id="spectrum-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="#ff0000" />
                <stop offset="16%" stop-color="#ffa500" />
                <stop offset="33%" stop-color="#ffff00" />
                <stop offset="50%" stop-color="#008000" />
                <stop offset="66%" stop-color="#0000ff" />
                <stop offset="83%" stop-color="#4b0082" />
                <stop offset="100%" stop-color="#ee82ee" />
             </linearGradient>
          </defs>

          <!-- 光栅组件线数模拟 -->
          <g transform="translate(100, 40)">
             <rect width="12" height="180" fill="#1e293b" rx="2" stroke="#334155" />
             <line v-for="n in Math.min(20, Math.floor(gratingDensity/10))" :key="n" 
                   x1="0" :y1="n * (180/(gratingDensity/10))" x2="12" :y2="n * (180/(gratingDensity/10))" 
                   stroke="#94a3b8" stroke-width="0.5" />
             <text x="-15" y="90" fill="#475569" font-size="8" transform="rotate(-90, -15, 90)" text-anchor="middle">GRATING (d={{ (1000/gratingDensity).toFixed(1) }}μm)</text>
          </g>

          <!-- 入射光束 -->
          <rect x="0" y="125" width="100" height="10" :fill="isWhiteLight ? '#f8fafc' : spectralColor" opacity="0.6" filter="url(#glow-light)" />

          <!-- 衍射条纹 (Screen at x=350) -->
          <g transform="translate(350, 40)">
             <rect width="30" height="180" fill="#020617" stroke="#1e293b" />
             
             <!-- 零级中央极大 (k=0) -->
             <rect x="2" y="85" width="26" height="10" :fill="isWhiteLight ? '#fff' : spectralColor" shadow="0 0 10px white" />

             <!-- 一级、二级光谱 (k=±1, ±2) -->
             <template v-for="k in [1, -1, 2, -2]">
                <!-- 如果是单色光，显示锐利线条 -->
                <rect v-if="!isWhiteLight" :key="'m'+k"
                      x="2" :y="90 + k * getTheta(wavelength) * 2 - 2" 
                      width="26" height="4" :fill="spectralColor" :opacity="1.2 - Math.abs(k)*0.4"
                      filter="url(#glow-light)" />
                
                <!-- 如果是白光，显示连续色散带 -->
                <rect v-else :key="'s'+k"
                      x="2" :y="90 + k * getTheta(400) * 2" 
                      width="26" :height="Math.abs(getTheta(700) - getTheta(400)) * 2" 
                      :fill="`url(#spectrum-grad)`" :opacity="1.1 - Math.abs(k)*0.4" 
                      :transform="k < 0 ? `scale(1, -1) translate(0, -180)` : ''" />
             </template>
          </g>

          <!-- 衍射包络线 (强度分布) -->
          <path :d="intensityPath" fill="none" stroke="#1e293b" stroke-width="1.5" stroke-dasharray="2,2" />

          <text x="50" y="30" fill="#475569" font-size="9" font-weight="900">INCIDENT BEAM</text>
          <text x="365" y="30" fill="#475569" font-size="9" font-weight="900" text-anchor="middle" transform="rotate(90, 365, 30)">SCREEN</text>
        </svg>

        <div class="physics-caption">
           {{ isWhiteLight ? '白光色散：波长越长，衍射角 θ 越大' : '单色相消干涉：极小值区光强几乎为零' }}
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">光栅常数 (线数/mm)</label>
                 <el-slider v-model="gratingDensity" :min="100" :max="1000" size="small"></el-slider>
              </div>
              <div class="flex-1">
                 <label class="s-label">入射波长 (nm)</label>
                 <el-slider v-model="wavelength" :min="400" :max="750" :disabled="isWhiteLight" size="small"></el-slider>
              </div>
              <el-checkbox v-model="isWhiteLight" size="small">复色白光模式</el-checkbox>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-picture-outline-round"></i>
           <p><strong>核心揭示：</strong>光栅是精密的“波长漏斗”。由于多缝干涉，只有在特定角度满足 $ d \sin\theta = k\lambda $ 时，各缝发出的光波才会步调一致增强。<strong>注意观察：</strong>增加线数 $ N $，光谱会更加延展。这解释了为何天文望远镜使用光栅而非棱镜来解析恒星化学成分——它能将极其相近的谱线分离开来。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'DiffractionGratingViz',
  data() {
    return {
      wavelength: 633,
      gratingDensity: 300,
      isWhiteLight: true
    }
  },
  computed: {
    spectralColor() {
       const w = this.wavelength;
       if (w < 440) return '#7e22ce';
       if (w < 490) return '#3b82f6';
       if (w < 570) return '#22c55e';
       if (w < 590) return '#eab308';
       if (w < 620) return '#f97316';
       return '#ef4444';
    },
    intensityPath() {
       // Mock of the grating intensity envelope |sin(Nx)/sin(x)|^2
       let p = "M 320 40 ";
       for (let y = 40; y <= 220; y += 2) {
          const dy = Math.abs(y - 130);
          const env = Math.exp(-dy * 0.02); // Primary envelope
          let comb = 0;
          [0, 1, -1, 2, -2].forEach(k => {
             const dist = Math.abs(dy - k * this.getTheta(this.wavelength) * 2);
             comb += Math.pow(Math.max(0, 1 - dist/8), 4);
          });
          p += `L ${320 - comb * 30 * env} ${y} `;
       }
       return p;
    }
  },
  methods: {
    getTheta(w) {
       // Simplified angle calc: sin(theta) = lambda / d
       // y_offset approx D * lambda * N
       return (w / 1000) * (this.gratingDensity / 100) * 15;
    }
  }
}
</script>

<style scoped lang="scss">
.grating-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fdf2f8; color: #be185d; border: 1px solid #fbcfe8; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 260px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #ec4899; } } }
.physics-caption { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex { display: flex; } .items-center { align-items: center; } .flex-row { flex-direction: row; } .flex-1 { flex: 1; } .gap-6 { gap: 16px; }
.discovery-card-compact { background: #fff1f2; padding: 10px 14px; border-radius: 10px; border: 1px solid #fecdd3; display: flex; align-items: center; gap: 10px; i { color: #e11d48; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #9f1239; line-height: 1.5; strong { color: #881337; } } }
</style>
